/**
 * Christmas Gift Finder -- a few questions, then the gifts that match.
 *
 * Everything this file needs comes from the JSON block the section renders:
 * the questions and their answer options, built from the section's blocks in
 * the theme editor, and the tagged gifts read off product metafields. There is
 * nothing about recipients, budgets or priorities in here -- a question knows
 * whether it matches a metafield or a price range, and whether it may be
 * relaxed, and that is all the logic needs.
 *
 * Loaded as a module: `matchGifts` is exported so the results logic can be
 * exercised on its own, and the custom element only registers itself when
 * there is a DOM to register it in.
 */

/** The option a customer picked for one question, or undefined. */
function chosen(question, slug) {
  return question.options.find((option) => option.slug === slug);
}

/** Does this gift satisfy one answered question? */
export function satisfies(gift, question, slug) {
  const option = chosen(question, slug);
  if (!option) return false;

  if (question.match === 'price') {
    // Lower bound exclusive, upper bound inclusive, so consecutive brackets
    // tile exactly: a gift at £15.00 is "Under £15", not also "£15 - £25".
    const min = option.min == null ? -Infinity : option.min;
    const max = option.max == null ? Infinity : option.max;
    return gift.rrp > min && gift.rrp <= max;
  }

  const tags = (gift.tags && gift.tags[question.field]) || [];
  return tags.includes(option.tag);
}

/**
 * The results logic (brief, section 4).
 *
 * Gifts satisfying every question come first ("exact"), then gifts satisfying
 * only the questions that cannot be relaxed, to fill the remaining slots
 * ("close"). Which questions may be relaxed is a per-question setting: in the
 * shipped flow the priority question can be, and recipient and budget cannot,
 * because a gift over the customer's budget or tagged for the wrong person is
 * worse than two results instead of three.
 *
 * Gifts are considered in the order they arrive, which is collection order, so
 * the same answers always give the same gifts in the same order.
 *
 * Returns `exact` as the number of exact matches among the gifts actually
 * returned, so the widget can label each gift "Exact match" or "Close option"
 * without overstating how precise the result is.
 *
 * An answer option can pin a gift ("Always show this product"): choosing that
 * answer always returns the gift, whatever the other answers (build
 * directions, section 4 -- Husband/Partner has a single gift). The pinning
 * answer counts as matched for it. If it also matches every other answer it
 * is an ordinary exact match; if not, it is returned last as `caveat`, so the
 * widget can introduce it as a suggestion rather than a match.
 */
export function matchGifts({ gifts, questions, answers, limit = 3 }) {
  const answered = questions.length > 0 && answers.length === questions.length;
  if (!answered || questions.some((question, i) => !chosen(question, answers[i]))) {
    return { results: [], exact: 0, caveat: null };
  }

  const pins = questions
    .map((question, i) => ({ index: i, gift: chosen(question, answers[i]).pin }))
    .filter((pin) => pin.gift);
  const pinned = pins.map((pin) => pin.gift.handle);
  const pool = gifts.filter((gift) => !pinned.includes(gift.handle));

  const required = pool.filter((gift) =>
    questions.every((question, i) => question.relaxable || satisfies(gift, question, answers[i]))
  );
  const exact = required.filter((gift) =>
    questions.every((question, i) => satisfies(gift, question, answers[i]))
  );
  const close = required.filter((gift) => !exact.includes(gift));

  // Pinned gifts are exact when they match every answer bar the one pinning them.
  const pinnedExact = [];
  const pinnedCaveat = [];
  pins.forEach(({ index, gift }) => {
    const matches = questions.every((question, i) => i === index || satisfies(gift, question, answers[i]));
    (matches ? pinnedExact : pinnedCaveat).push(gift);
  });

  const allExact = pinnedExact.concat(exact);
  const caveat = pinnedCaveat[0] || null;
  const room = Math.max(limit - (caveat ? 1 : 0), 0);
  const results = allExact.concat(close).slice(0, room).concat(caveat ? [caveat] : []);

  return {
    results,
    exact: Math.min(allExact.length, room),
    caveat: caveat ? caveat.handle : null,
  };
}

/** `mum-under-15-luxury` -- the shareable form of an answer set. */
export function encodeAnswers(answers) {
  return answers.join('-');
}

/**
 * Reads answers back out of that form. Slugs may contain hyphens, so they are
 * matched longest-first against each question's own options rather than split
 * on the separator.
 */
export function decodeAnswers(value, questions) {
  let rest = String(value || '').toLowerCase();
  const answers = [];

  for (const question of questions) {
    const slugs = question.options
      .map((option) => option.slug)
      .sort((a, b) => b.length - a.length);
    const hit = slugs.find((slug) => rest === slug || rest.startsWith(`${slug}-`));
    if (!hit) return [];

    answers.push(hit);
    rest = rest.slice(hit.length + 1);
  }

  return rest === '' ? answers : [];
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// Importable outside a browser, where there is no HTMLElement to extend.
const CustomElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class GiftFinderWidget extends CustomElementBase {
  connectedCallback() {
    const configEl = this.querySelector('[data-gf-config]');
    if (!configEl) return;

    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (error) {
      return;
    }

    this.body = this.querySelector('[data-gf-body]');
    this.titleEl = this.querySelector('[data-gf-title]');
    this.stepEl = this.querySelector('[data-gf-step]');
    this.answers = [];
    this.addEventListener('click', this.onClick.bind(this));
    window.addEventListener('popstate', this.onPopState.bind(this));

    // A shared link lands here with its answers in the query string. Nothing
    // server-rendered depends on them -- they exist so a result can be sent to
    // someone.
    const shared = decodeAnswers(this.readParam(), this.config.questions);
    if (shared.length === this.config.questions.length) {
      this.answers = shared;
      this.showResults();
    }
  }

  readParam() {
    return new URLSearchParams(window.location.search).get(this.config.param);
  }

  onClick(event) {
    const option = event.target.closest('[data-gf-option]');
    if (option) {
      event.preventDefault();
      this.answer(option.getAttribute('data-gf-option'));
      return;
    }

    if (event.target.closest('[data-gf-back]')) {
      event.preventDefault();
      this.back();
      return;
    }

    if (event.target.closest('[data-gf-restart]')) {
      event.preventDefault();
      this.restart();
    }
  }

  answer(slug) {
    this.answers.push(slug);

    if (this.answers.length < this.config.questions.length) {
      this.renderQuestion();
      this.focusTitle();
      return;
    }

    this.showResults();
    this.focusTitle();
    this.pushParam(encodeAnswers(this.answers));
  }

  /** One question back, with the answer given there still shown as picked. */
  back() {
    if (this.answers.length === 0) return;
    const previous = this.answers.pop();
    this.renderQuestion(previous);
    this.focusTitle();
  }

  restart() {
    this.answers = [];
    this.renderQuestion();
    this.focusTitle();
    this.pushParam(null);
  }

  /** Moves focus to the new heading, so keyboard and screen reader users follow along. */
  focusTitle() {
    if (this.titleEl) this.titleEl.focus({ preventScroll: true });
  }

  setHeading(title, stepLabel) {
    if (this.titleEl) this.titleEl.textContent = title;
    if (this.stepEl) this.stepEl.textContent = stepLabel;
  }

  renderQuestion(selected) {
    const step = this.answers.length;
    const total = this.config.questions.length;
    const question = this.config.questions[step];
    if (!question || !this.body) return;

    const stepLabel = (this.config.copy.step || '')
      .replace('[current]', step + 1)
      .replace('[total]', total);
    this.setHeading(question.text, stepLabel);

    const list = element('ul', 'gift-finder__options');
    question.options.forEach((option) => {
      const button = element('button', 'gift-finder__option', option.label);
      button.type = 'button';
      button.setAttribute('data-gf-option', option.slug);
      if (selected) button.setAttribute('aria-pressed', String(option.slug === selected));
      list.appendChild(element('li')).appendChild(button);
    });

    this.body.className = 'gift-finder__body';
    this.body.textContent = '';
    this.body.appendChild(list);
    if (step > 0) this.body.appendChild(this.backButton());
    this.progress(step);
  }

  backButton() {
    const row = element('div', 'gift-finder__back-row');
    const button = element('button', 'gift-finder__back');
    button.type = 'button';
    button.setAttribute('data-gf-back', '');
    button.innerHTML =
      '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M7.5 2.5L4 6l3.5 3.5"></path></svg>';
    button.appendChild(document.createTextNode(this.config.copy.back || 'Back'));
    row.appendChild(button);
    return row;
  }

  showResults() {
    if (!this.body) return;

    const { results, exact, caveat } = matchGifts({
      gifts: this.config.gifts,
      questions: this.config.questions,
      answers: this.answers,
      limit: this.config.limit,
    });

    this.setHeading(this.config.copy.resultsHeading, '');
    this.body.className = 'gift-finder__body gift-finder__body--results';
    this.body.textContent = '';
    this.body.appendChild(this.answerSummary());

    if (results.length === 0) {
      // Never an empty state (section 4): say so, and leave a way on.
      this.body.appendChild(element('p', 'gift-finder__fallback', this.config.copy.fallback));
    } else {
      // A pinned gift that misses some answers is introduced on its own, after
      // the matches, so it reads as a suggestion rather than a match.
      const matched = caveat ? results.slice(0, -1) : results;

      if (matched.length > 0) {
        const list = element('ul', 'gift-finder__results');
        matched.forEach((gift, index) => list.appendChild(this.resultRow(gift, index < exact)));
        this.body.appendChild(list);
      }

      if (caveat) {
        this.body.appendChild(element('p', 'gift-finder__note', this.config.copy.caveat));
        const list = element('ul', 'gift-finder__results');
        list.appendChild(this.resultRow(results[results.length - 1], false));
        this.body.appendChild(list);
      }
    }

    this.body.appendChild(this.actions());
    this.progress(this.config.questions.length);
  }

  /** One gift card: image, Exact match / Close option tag, name and price. */
  resultRow(gift, isExact) {
    const row = element('li', 'gift-finder__result');
    const link = element('a', 'gift-finder__result-link');
    link.href = gift.url;

    if (gift.image) {
      // Decorative: the product name is in the link text.
      const image = element('img', 'gift-finder__thumb');
      image.src = gift.image;
      image.alt = '';
      image.width = 400;
      image.height = 400;
      image.loading = 'lazy';
      link.appendChild(image);
    } else {
      // Placeholder gradient, per the brand guidelines.
      const placeholder = element('span', 'gift-finder__thumb');
      placeholder.setAttribute('aria-hidden', 'true');
      link.appendChild(placeholder);
    }

    const detail = element('span', 'gift-finder__result-body');
    const tag = isExact ? this.config.copy.exactTag : this.config.copy.closeTag;
    if (tag) detail.appendChild(element('span', 'gift-finder__tag', tag));
    detail.appendChild(element('span', 'gift-finder__result-name', gift.title));

    const price = element('span', 'gift-finder__result-price', gift.priceLabel);
    if (gift.rrpLabel && gift.rrp > gift.price) {
      price.appendChild(document.createTextNode(' '));
      price.appendChild(element('s', null, gift.rrpLabel));
    }
    detail.appendChild(price);

    link.appendChild(detail);
    row.appendChild(link);
    return row;
  }

  actions() {
    const actions = element('div', 'gift-finder__actions');

    if (this.config.copy.shopUrl) {
      const shop = element('a', 'gift-finder__cta', this.config.copy.shop);
      shop.href = this.config.copy.shopUrl;
      actions.appendChild(shop);
    }

    const again = element('button', 'gift-finder__ghost', this.config.copy.restart);
    again.type = 'button';
    again.setAttribute('data-gf-restart', '');
    actions.appendChild(again);

    return actions;
  }

  answerSummary() {
    const labels = this.answers.map((slug, index) => {
      const option = chosen(this.config.questions[index], slug);
      return option ? option.label : slug;
    });

    const summary = element('p', 'gift-finder__answers', `${this.config.copy.matchedTo} `);
    summary.appendChild(element('strong', null, labels.join(', ')));
    return summary;
  }

  progress(step) {
    const bars = this.querySelectorAll('[data-gf-progress] span');
    bars.forEach((bar, index) => {
      if (index < step) bar.setAttribute('data-state', 'done');
      else if (index === step) bar.setAttribute('data-state', 'current');
      else bar.removeAttribute('data-state');
    });
  }

  /** Answers in the query string, so a result can be shared or bookmarked. */
  pushParam(value) {
    if (!window.history || !window.history.pushState) return;

    const url = new URL(window.location.href);
    if (value) url.searchParams.set(this.config.param, value);
    else url.searchParams.delete(this.config.param);

    if (url.href !== window.location.href) window.history.pushState({}, '', url.href);
  }

  onPopState() {
    const shared = decodeAnswers(this.readParam(), this.config.questions);

    if (shared.length === this.config.questions.length) {
      this.answers = shared;
      this.showResults();
      return;
    }

    this.answers = [];
    this.renderQuestion();
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('gift-finder-widget')) {
  customElements.define('gift-finder-widget', GiftFinderWidget);
}

export { GiftFinderWidget };
